import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface MtdPair { allegation_ref: string; allegation: string; instrument: string; provision: string; grant_text: string; effect: string; strength: string; }
export interface MtdCount { ordinal: number; count_name: string; verdict: string; defects: string[]; rationale: string; }
export interface MtdDefense { defense: string; applies_to: string; assessment: string; rationale: string; }
export interface MtdSynth { ordinal: number; count_name: string; predicted_outcome: string; falling_theories: string[]; surviving_theory: string; drivers: string; reasoning: string; }
export interface MtdNotice { allegation_ref: string; allegation: string; record: string; quote: string; effect: string; notice_basis: string; strength: string; }
export interface GapItem { reference: string; description: string; cited_at: string; in_corpus: boolean; matched_title: string; public_record: boolean; source_hint: string; }
export interface AnswerResponse { paragraph_number: number; response: string; detail: string; rationale: string; evidence_anchor: string; }
export interface DefenseSupport { authority_id: string; label: string; title: string; locator: string; evidence_id: string | null; instrument_type: string; }
export interface DefenseEvidence { chunk_id: string; source_label: string | null; occurred_on: string | null; quote: string; paragraph_number: number | null; }
export interface AnswerDefense {
  defense: string; definition: string; must_prove: string[];
  grounds: string; basis: string; applies_to: string; strength: string;
  supports?: DefenseSupport[]; evidence?: DefenseEvidence[];
}
export interface AnswerCounterclaim {
  claim_name: string; definition?: string; must_prove?: string[];
  theory: string; grounds?: string; compulsory: boolean; basis: string;
  evidence_basis?: string; caution: string;
  supports?: DefenseSupport[]; evidence?: DefenseEvidence[];
}
export interface AmendDiffItem {
  kind: string; severity: string;
  original_text: string; amended_text: string; amended_ref: string;
  significance: string; evidence?: DefenseEvidence[];
}
export interface DefensePrecedent {
  id: string; category: string; keywords: string[];
  case_name: string; citation: string; court: string;
  decided_on: string | null; posture: string | null; outcome: string;
  holding: string; takeaway: string; source_url: string; verified_on: string;
}

export interface MtdProng { key: string; title: string; intro: string; items: unknown[]; }
export interface StrikeContent {
  kind: string; generated_at: string; build: string; basis: string;
  prongs: MtdProng[]; failures?: string[]; caveats: string;
}
export interface StrikeArtifact {
  id: string; artifact_kind: string; title: string; status: string;
  created_at: string; content: StrikeContent;
}

export function useStrikeArtifacts(caseId: string | undefined, poll: boolean) {
  return useQuery<StrikeArtifact[], Error>({
    queryKey: ["strike-artifacts", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    refetchInterval: poll ? 4000 : false,
    staleTime: poll ? 0 : 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_strike_artifacts", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as StrikeArtifact[]) ?? [];
    },
  });
}

/** The curated, verified NC precedent library (citations never LLM-generated). */
export function useDefensePrecedents() {
  return useQuery<DefensePrecedent[], Error>({
    queryKey: ["defense-precedents"],
    enabled: isSupabaseConfigured(),
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("fetch_defense_precedents", {});
      if (error) throw new Error(error.message);
      return (data as DefensePrecedent[]) ?? [];
    },
  });
}

/** Purge an outdated artifact (case-role gated server-side). */
export function usePurgeStrikeArtifact(caseId: string | undefined) {
  const qc = useQueryClient();
  const [purging, setPurging] = useState<string | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const purge = useCallback(async (artifactId: string) => {
    if (purging) return;
    setPurgeError(null);
    setPurging(artifactId);
    try {
      const { data, error } = await berserkr().rpc("delete_strike_artifact", { p_artifact_id: artifactId });
      if (error) throw new Error(error.message);
      if (!(data as { deleted?: boolean })?.deleted) throw new Error("artifact not found");
      qc.invalidateQueries({ queryKey: ["strike-artifacts", caseId] });
    } catch (e) {
      setPurgeError(e instanceof Error ? e.message : String(e));
    } finally {
      setPurging(null);
    }
  }, [purging, caseId, qc]);

  return { purge, purging, purgeError };
}

/** Forge a strike artifact (background 202); poll the list until the new one lands. */
export function useForgeStrikeArtifact(caseId: string | undefined) {
  const qc = useQueryClient();
  const [forging, setForging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseline = useRef<string | null>(null);

  const list = useStrikeArtifacts(caseId, forging);
  const newest = list.data?.[0]?.created_at ?? null;

  useEffect(() => {
    if (!forging) return;
    if (newest && newest !== baseline.current) {
      setForging(false);
      qc.invalidateQueries({ queryKey: ["strike-artifacts", caseId] });
    }
  }, [forging, newest, caseId, qc]);

  useEffect(() => {
    if (!forging) return;
    const t = setTimeout(() => {
      setForging(false);
      setError("forge is taking longer than expected — refresh shortly");
    }, 240 * 1000); // the answer skeleton batches all 165 ¶s — give it room
    return () => clearTimeout(t);
  }, [forging]);

  const forge = useCallback(async (kind: string) => {
    if (!caseId || forging) return;
    const sb = getSupabase();
    if (!sb) { setError("Supabase not configured"); return; }
    setError(null);
    baseline.current = newest;
    setForging(true);
    const { error: e } = await sb.functions.invoke("forge-strike-artifact", { body: { case_id: caseId, kind } });
    if (e) { setError(e.message); setForging(false); }
  }, [caseId, forging, newest]);

  return { forge, forging, error, artifacts: list.data ?? [], loading: list.isLoading, listError: list.error };
}
